import {
    Attachment,
    Badge,
    Button,
    ButtonGroup,
    cleanObject,
    dispatchCustomEvent,
    FDate,
    FIDescription,
    getKey,
    Heading,
    Icon,
    IconLib,
    Input,
    Link,
    logger,
    Menu,
    MenuHeading,
    MenuItem,
    Notification,
    NotificationContent,
    NotificationIcon,
    Popover,
    Portal,
    Resizable,
    Sidebar,
    stopEvent,
    Text,
    Tooltip,
    UploadArea,
    useCustomEvent,
    useDialog,
    useStorage,
    useToast,
    useVisibility,
    View,
} from '@fold-dev/core'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
    TbAlertSquareRounded,
    TbArrowLeft,
    TbArrowUp,
    TbArrowUpRight,
    TbCheck,
    TbChecklist,
    TbFlag,
    TbChevronDown,
    TbChevronUp,
    TbCopy,
    TbDots,
    TbExclamationCircle,
    TbExclamationMark,
    TbFileUpload,
    TbFlag2,
    TbMail,
    TbPaperclip,
    TbShare,
    TbTrash,
} from 'react-icons/tb'
import { generatePath, useNavigate, useParams } from 'react-router'
import { v4 as uuidv4 } from 'uuid'
import {
    Bars,
    buildTaskTree,
    Check,
    CommonProvider,
    DateButton,
    flattenArray,
    LABEL_CREATE_PREFIX,
    LabelButton,
    ListButton,
    Loading,
    PriorityButton,
    RichInput,
    RichInputOption,
    RichInputProvider,
    UserButton,
    Users,
} from '../../components'
import { processHTML } from '../../components/rich-input/rich-input.util'
import {
    byName,
    byPosition,
    byText,
    colors,
    copyToClipboard,
    getCachedProjectView,
    getMentionsfromTipTapJSON,
    getRandomColor,
    removeExistingElements,
    removeURLTask,
    replaceHTMLEntities,
    searchLabels,
    setEndOfContentEditable,
    stripS3Url,
} from '../../helpers'
import { getNewCompletedTaskDate } from '../../helpers/dates'
import {
    useLabels,
    useProjectSections,
    useProjectUsers,
    useTaskAttachments,
    useTaskFollowers,
    useTaskLabels,
    useTasks,
    useTaskUsers,
    useTeamUsers,
    useWorkspaceUsers,
} from '../../hooks/api.hooks'
import { useAuth } from '../../hooks/auth.hook'
import { useFile } from '../../hooks/file.hook'
import { useSubscription } from '../../hooks/websocket.hook'
import { useAppStore } from '../../store/app'
import { Tiptap, TIPTAP_CONTENT_SAVE_TIMEOUT } from '../tiptap'
import { Comments } from './comments'
import { List } from './list'
import { Screenshot } from './screenshot'

export const SidebarArea = (props: any) => {
    const { getStorage, setStorage } = useStorage()
    const width = useMemo(() => +getStorage('FRONTEER_TASK_SIDEBAR_WIDTH') || '60%', [])

    const handleNavChange = (width) => {
        setStorage('FRONTEER_TASK_SIDEBAR_WIDTH', width)
    }

    return (
        <Resizable
            column
            position="end"
            alignItems="flex-start"
            alignContent="flex-start"
            justifyContent="flex-start"
            onChange={handleNavChange}
            width={width}
            max={2000}
            min={250}
            height="100%"
            bg="transparent"
            handle={<></>}
            railProps={{ style: { '--f-resizable-color': 'transparent', 'zIndex': 2000 } }}>
            <View
                column
                position="absolute"
                width="100%"
                height="100%"
                justifyContent="flex-start"
                style={{
                    inset: 0,
                }}>
                {props.children}
            </View>
        </Resizable>
    )
}

const TaskAttachment = ({ attachment, onDelete, onScreenshotView }) => {
    const { name, type, url } = attachment
    const { getDownloadUrl } = useFile()
    const { setDialog, closeDialog } = useDialog()
    const isScreenshot = useMemo(() => name.split('.').includes('screenshot'), [name])
    const [loading, setLoading] = useState(false)

    const handleUrlClick = async (e) => {
        try {
            stopEvent(e)

            const downloadLink = await getDownloadUrl({ filename: url, secure: true })

            if (isScreenshot) {
                setLoading(true)

                const response = await fetch(downloadLink.url)
                const text = await response.text()
                const data = JSON.parse(text)

                setLoading(false)
                onScreenshotView(data)
            } else {
                window.open(downloadLink.url)
            }
        } catch (e) {
            setLoading(false)
            console.error(e)
        }
    }

    const handleDelete = async (e) => {
        stopEvent(e)

        setDialog({
            title: 'Are you sure?',
            description: 'This action cannot be undone.',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        onClick={() => {
                            onDelete()
                            closeDialog()
                        }}
                        variant="danger">
                        Delete
                    </Button>
                </View>
            ),
        })
    }

    return (
        <Attachment
            as="a"
            loading={loading}
            loadingProgress={100}
            width={200}
            mime={isScreenshot ? 'image/png' : type}
            filesize={24325}
            label={isScreenshot ? 'Screenshot' : name}
            onClick={handleUrlClick}
            tools={
                <div
                    className="f-row"
                    style={{ gap: 5 }}>
                    <Icon
                        icon={TbArrowUpRight}
                        style={{ color: 'var(--f-color-base-300)' }}
                    />
                    <span
                        className="f-buttonize"
                        onClick={handleDelete}>
                        <Icon
                            icon={TbTrash}
                            style={{ color: 'var(--f-color-base-300)' }}
                        />
                    </span>
                </div>
            }
        />
    )
}

const TaskToolbar = ({ onFileUpload, onDismiss, location = undefined, urlPattern = '' }) => {
    const { workspaceId, taskId, view } = useParams()
    const { visible, show, hide } = useVisibility()
    const [error, setError] = useState(null)
    const task = useAppStore((state) => state.task)
    const sections = useAppStore((state) => state.sections)
    const { id, type, projectId, projectSectionId, projectSectionTitle, projectName, points, complexity } = task
    const actions = useAppStore((state) => state.actions)
    const { setDialog, closeDialog } = useDialog()
    const tasksAPI = useTasks()
    const navigate = useNavigate()
    const { sectionTasks, currentIndex, isFirst, isLast } = useMemo(() => {
        const section = sections.find((section) => flattenArray(section.tasks).some((task) => task.id == id))
        const sectionTasks = section?.tasks ? flattenArray(section.tasks) : []
        const currentIndex = sectionTasks.findIndex((task) => task.id == id)
        const isFirst = currentIndex == 0
        const isLast = !sectionTasks[currentIndex + 1]

        return {
            sectionTasks,
            currentIndex,
            isFirst,
            isLast,
        }
    }, [sections, task])
    const { isInbox, isUnassigned, isToday, isCalendar, isUpcoming, isProject } = useMemo(
        () => ({
            isInbox: location == 'inbox',
            isUnassigned: location == 'unassigned',
            isToday: location == 'today',
            isCalendar: location == 'calendar',
            isUpcoming: location == 'upcoming',
            isProject: location == 'project',
        }),
        [location]
    )
    const [history, setHistory] = useState([])
    const fileRef = useRef<any>(null)
    const { showToast } = useToast()
    const historyTitle = useMemo(() => {
        if (!history.length) return ''
        if (!projectId) return 'Unassigned'

        const prefix = `${projectName} ${!!projectSectionTitle ? '/' : ''} ${projectSectionTitle || ''}`

        if (history.length == 1) return prefix

        return (
            prefix +
            ' / ' +
            history
                .slice(0, -1)
                .map((h) => replaceHTMLEntities(h.title))
                .join(' / ')
        )
    }, [history, projectName, projectSectionTitle, projectId])

    const handlePrevious = (e) => {
        navigate(
            generatePath(urlPattern, {
                taskId: sectionTasks[currentIndex - 1].id,
                workspaceId,
                projectId,
                view,
            })
        )
    }

    const handleNext = (e) => {
        navigate(
            generatePath(urlPattern, {
                taskId: sectionTasks[currentIndex + 1].id,
                workspaceId,
                projectId,
                view,
            })
        )
    }

    const handleFileChange = async (e) => {
        if (e.target.files.length != 0) onFileUpload(e.target.files)
    }

    const handleFileClick = (e) => (fileRef.current ? fileRef.current.click() : null)

    const handleCopy = () => {
        const url = projectId
            ? `${window.location.origin}/app/workspace/${workspaceId}/c/${projectId}/${getCachedProjectView(
                  projectId
              )}/task/${id}`
            : window.location.href.split('?')[0]
        copyToClipboard(url)
        showToast({ message: 'URL copied to clipboard.', anchor: 'bottom-right' })
    }

    const handleHistoryBack = () => {
        const previousTask = history[history.length - 2]

        if (previousTask?.id) {
            navigate(
                generatePath(urlPattern, {
                    taskId: previousTask.id,
                    workspaceId,
                    projectId,
                    view,
                })
            )
        } else {
            onDismiss()
        }
    }

    const handleDelete = () => {
        setDialog({
            title: 'Are you sure?',
            description: 'This action cannot be undone.',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        onClick={() => {
                            tasksAPI.remove(workspaceId, projectId, taskId)
                            actions.taskDelete({ task: { id } })
                            closeDialog()
                            onDismiss()
                        }}
                        variant="danger">
                        Delete
                    </Button>
                </View>
            ),
        })
    }

    const handleTypeChange = async (type) => {
        actions.taskUpdate({ task: { id, type } })

        await tasksAPI.update(workspaceId, projectId, taskId, { type })
    }

    const fetchData = async () => {
        setError(null)
        try {
            const history = await tasksAPI.getHistory(workspaceId, projectId, taskId)
            setHistory(history)
        } catch (e) {
            logger(e)
            setError(e.message)
        }
    }

    useEffect(() => {
        if (taskId) fetchData()
    }, [taskId, projectId, projectSectionId])

    return (
        <div className="task__toolbar f-row">
            {history.length == 1 && (
                <Icon
                    icon={TbChecklist}
                    style={{ top: 1 }}
                />
            )}

            {history.length > 1 && (
                <span
                    style={{ color: 'var(--f-color-text-weaker)' }}
                    className="f-buttonize"
                    onClick={handleHistoryBack}>
                    <Icon icon={TbArrowLeft} />
                </span>
            )}

            <div style={{ flex: 1, overflow: 'hidden' }}>
                <div
                    className="f-ellipsis"
                    style={{ width: 310, overflow: 'hidden' }}>
                    <Text
                        as="span"
                        colorToken="text-weaker"
                        fontWeight={600}>
                        {historyTitle}
                    </Text>
                </div>
            </div>

            {+task.likes != 0 && (
                <View
                    row
                    p="0 0.5rem">
                    <Icon icon={TbArrowUp} />
                    <Text colorToken="text-weaker">{task.likes}</Text>
                </View>
            )}

            <View p="0 0.75rem 0 0">
                <Bars complexity={complexity} />
            </View>

            <input
                multiple
                type="file"
                className="hide"
                ref={fileRef}
                onChange={handleFileChange}
            />

            <Button
                subtle
                size="xs"
                onClick={handleFileClick}>
                <Icon icon={TbPaperclip} />
            </Button>

            <Button
                subtle
                size="xs"
                onClick={() => {
                    handleCopy()
                    hide()
                }}>
                <Icon icon={TbCopy} />
            </Button>

            <Tooltip
                text="Milestone"
                anchor="bottom-center">
                <Button
                    subtle
                    bgToken={type == 'milestone' ? 'success-weak' : undefined}
                    size="xs"
                    variant={type == 'milestone' ? 'success' : undefined}
                    onClick={() => handleTypeChange(type == 'milestone' ? 'task' : 'milestone')}>
                    <Icon icon={TbFlag} />
                </Button>
            </Tooltip>

            <Tooltip
                text="Blocker"
                anchor="bottom-center">
                <Button
                    subtle
                    bgToken={type == 'blocker' ? 'danger-weak' : undefined}
                    variant={type == 'blocker' ? 'danger' : undefined}
                    size="xs"
                    onClick={() => handleTypeChange(type == 'blocker' ? 'task' : 'blocker')}>
                    <Icon icon={TbAlertSquareRounded} />
                </Button>
            </Tooltip>

            <Popover
                arrow
                focusTrap
                width="fit-content"
                anchor="bottom-right"
                content={
                    <Menu
                        border="none"
                        width={200}>
                        <MenuHeading>Task menu</MenuHeading>
                        <MenuItem
                            onClick={() => {
                                handleDelete()
                                hide()
                            }}
                            prefix={<Icon icon={TbTrash} />}>
                            Delete
                        </MenuItem>
                    </Menu>
                }
                isVisible={visible}
                onDismiss={hide}>
                <Button
                    subtle
                    size="xs"
                    onClick={show}>
                    <Icon icon={TbDots} />
                </Button>
            </Popover>

            {!isInbox && (
                <ButtonGroup m="0 0 0 0.5rem">
                    <Button
                        size="xs"
                        onClick={handlePrevious}
                        disabled={isFirst}>
                        <Icon icon={TbChevronUp} />
                    </Button>
                    <Button
                        size="xs"
                        onClick={handleNext}
                        disabled={isLast}>
                        <Icon icon={TbChevronDown} />
                    </Button>
                </ButtonGroup>
            )}

            <Button
                subtle
                size="xs"
                onClick={onDismiss}>
                <IconLib icon="x" />
            </Button>
        </div>
    )
}

export const Task = ({ direction = 'horizontal', location = undefined, urlPattern = '' }) => {
    const { workspaceId, taskId, view } = useParams()
    const editorRef = useRef(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const task = useAppStore((state) => state.task)
    const {
        id,
        title,
        description,
        priority,
        type,
        position,
        complete,
        points,
        complexity,
        start,
        end,
        repeat,
        wholeDay,
        externalEmail,
        meta,
        users,
        followers,
        labels,
        attachments,
        projectId,
        likes,
        projectSectionId,
        projectSectionTitle,
        projectName,
        teamId,
    } = task
    const actions = useAppStore((state) => state.actions)
    const user = useAppStore((state) => state.user)
    const [pointsInput, setPointsInput] = useState(points)
    const [pointsEdit, setPointsEdit] = useState(false)
    const [availableLabels, setAvailableLabels] = useState([])
    const [availableUsers, setAvailableUsers] = useState([])
    const workspaceUsersAPI = useWorkspaceUsers()
    const [options, setOptions] = useState<RichInputOption[]>([])
    const tasksAPI = useTasks()
    const { getUserId } = useAuth()
    const labelsAPI = useLabels()
    const timeout = useRef(null)
    const projectUsersAPI = useProjectUsers()
    const projectSectionsAPI = useProjectSections()
    const teamUsersAPI = useTeamUsers()
    const taskFollowersAPI = useTaskFollowers()
    const navigate = useNavigate()
    const taskLabelsAPI = useTaskLabels()
    const taskUsersAPI = useTaskUsers()
    const taskAttachmentsAPI = useTaskAttachments()
    const [screenshot, setScreenshot] = useState(null)
    const projects = useAppStore((state) => state.projects)
    const subtasks = useAppStore((state) => state.subtasks)
    const { upload } = useFile()
    const [sections, setSections] = useState([])
    const selectableProjects = useMemo(() => {
        return projects
            .filter(({ isPrivate }) => !isPrivate)
            .map(({ id, name }) => ({
                value: id ?? '',
                label: name,
            }))
    }, [projects])
    const selectedProject = useMemo(
        () => selectableProjects.findIndex((project) => project.value == projectId),
        [selectableProjects, projectId]
    )
    const selectedSection = useMemo(
        () => sections.findIndex((s) => s.value == task.projectSectionId),
        [sections, task.projectSectionId]
    )
    const { sort, hideComplete } = useAppStore((state: any) => state.filters)
    const isFollowing = useMemo(() => !!followers.find((u) => u.id == user.id), [followers, user])
    const { isInbox, isUnassigned, isToday, isCalendar, isUpcoming, isProject } = useMemo(
        () => ({
            isInbox: location == 'inbox',
            isUnassigned: location == 'unassigned',
            isToday: location == 'today',
            isCalendar: location == 'calendar',
            isUpcoming: location == 'upcoming',
            isProject: location == 'project',
        }),
        [location]
    )
    const complexityIndex = useMemo(() => {
        switch (complexity) {
            case 'low':
                return 1
            case 'medium':
                return 2
            case 'high':
                return 3
            default:
                return 0
        }
    }, [complexity])

    const handleFileUpload = async (rawFiles) => {
        setLoading(true)
        setError(null)

        try {
            const fileArray: any = Array.from(rawFiles)
            const fileUploads: any = await Promise.all(
                fileArray.map((file: any) => {
                    return upload({
                        folder: workspaceId,
                        file,
                        secure: true,
                    })
                })
            )
            const files = fileUploads.map(({ url }, index) => {
                return {
                    name: fileArray[index].name,
                    filesize: fileArray[index].size,
                    url,
                    type: fileArray[index].type,
                }
            })

            const attachments = files.map((file) => ({
                name: file.name,
                filesize: file.filesize,
                url: stripS3Url(file.url),
                type: file.type,
            }))

            const createdAttachments = await taskAttachmentsAPI.create(workspaceId, projectId, taskId, attachments)

            actions.taskUpdate({ task: { id, ...task, attachments: [...task.attachments, ...createdAttachments] } })
            // actions.taskUpdate({ task: { id, attachments: task.attachments.length + 1 } })

            setLoading(false)
        } catch (e) {
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleComplete = async (complete) => {
        const updatedTask = getNewCompletedTaskDate({ complete, start, end, repeat, wholeDay })

        // for repeat tasks that have a next date (they don't get marked as checked)
        if (!updatedTask.complete && complete) dispatchCustomEvent('faux-check', { id })

        actions.taskUpdate({ task: { id, ...updatedTask } })

        await tasksAPI.update(workspaceId, projectId, taskId, updatedTask)
    }

    const handlePointsUpdate = async (points) => {
        actions.taskUpdate({ task: { id, points } })
        await tasksAPI.update(workspaceId, projectId, taskId, { points })
    }

    const handleComplexityUpdate = async (value) => {
        let complexity = null

        switch (+value) {
            case 1:
                complexity = 'low'
                break
            case 2:
                complexity = 'medium'
                break
            case 3:
                complexity = 'high'
                break
            default:
                complexity = null
                break
        }

        actions.taskUpdate({ task: { id, complexity } })

        await tasksAPI.update(workspaceId, projectId, taskId, { complexity })
    }

    const handleDateChange = async ({ start, end, repeat, wholeDay }) => {
        actions.taskUpdate({ task: { id, start, end, repeat, wholeDay } })

        await tasksAPI.update(workspaceId, projectId, taskId, {
            start: start ? FDate(start).toISOWithTimezone() : null,
            end: end ? FDate(end).toISOWithTimezone() : null,
            repeat,
            wholeDay,
        })
    }

    const handleDateDelete = async () => {
        const start = null
        const end = null
        const repeat = null
        const wholeDay = true

        actions.taskUpdate({ task: { id, start, end, repeat, wholeDay } })

        await tasksAPI.update(workspaceId, projectId, taskId, { start, end, repeat, wholeDay })
    }

    const handleSectionChange = async (sectionIndex) => {
        const section = sections[sectionIndex]
        const projectSectionId = section.value == '' ? null : section.value
        const projectSectionTitle = section.value == '' ? null : section.label

        if (projectSectionId == task.projectSectionId) return

        actions.taskUpdateMany({
            taskIds: [id, ...subtasks.map(({ id }) => id)],
            task: {
                projectSectionId,
                projectSectionTitle,
            },
        })

        // update this task + subtasks
        tasksAPI.updateMany(workspaceId, projectId, [
            { id, projectSection: { id: projectSectionId } },
            ...subtasks.map(({ id }) => ({ id, projectSection: { id: projectSectionId } })),
        ])
    }

    /* 
    const handleProjectChange = async (index) => {
        const projectId = selectableProjects[index]?.value

        if (projectId == task.projectId) return

        const project = projects.find((project) => project.id == projectId)
        const taskId = id
        const tasks = [task, ...subtasks]
        const taskIds = [taskId, ...(tasks || []).map((t) => t.id)]

        if (isUpcoming || isToday || isCalendar) {
            actions.taskUpdateProject({
                taskIds,
                task: {
                    projectId,
                    projectName: project.name,
                    projectColor: project.color,
                },
            })

            tasksAPI.moveToProject(
                workspaceId,
                projectId,
                taskIds.map((taskId) => ({ id: taskId }))
            )
        } else {
            // 1) remove it from the current project (behind the modal)
            actions.taskDelete({ task })

            // 2) add them to the new project
            actions.taskAddMultiple({
                sectionId: null,
                tasks,
                syncWithProjectId: projectId,
            })

            // 3) update this task
            actions.taskUpdate({
                task: {
                    id,
                    projectId,
                    projectName: project.name,
                    projectColor: project.color,
                    projectSectionId: null,
                    projectSectionTitle: null,
                },
            })

            // 4) do the API call
            tasksAPI.moveToProject(
                workspaceId,
                projectId,
                tasks.map(({ id }) => ({ id }))
            )

            // 5) reload the sections
            populateSections(projectId)
        }
    }
    */
   
    const handleLabelAdd = async (label) => {
        const newLabelId = uuidv4()
        const taskLabelId = uuidv4()
        const taskId = task.id
        let labels = task.labels || []
        let labelId = label.id
        let newLabel = null

        // create the label if it doesn't exist
        if (!label.id) {
            newLabel = {
                id: newLabelId,
                color: label.color,
                text: label.text.replace(LABEL_CREATE_PREFIX, ''),
                description: '',
            }

            labelId = newLabel.id
            labels.push({ ...newLabel, tlid: taskLabelId })
        } else {
            labels.push({ ...label, tlid: taskLabelId })
        }

        actions.taskUpdate({ task: { id, labels } })

        if (!!newLabel) {
            actions.labelsAdd(newLabel)
            await labelsAPI.create(workspaceId, newLabel)
        }

        await taskLabelsAPI.create(workspaceId, projectId, taskId, { id: taskLabelId, labelId })
    }

    const handleLabelDelete = async (label) => {
        const taskLabelId = label.tlid
        const newLabels = labels.filter((l) => label.id != l.id)

        actions.taskUpdate({ task: { id: taskId, labels: newLabels } })

        await taskLabelsAPI.remove(workspaceId, projectId, taskId, taskLabelId)
    }

    const handleUserAdd = async (user) => {
        const id = uuidv4()
        const userId = user.id
        const newUsers = [...users, { ...user, tuid: id }]

        actions.taskUpdate({ task: { id: taskId, users: newUsers } })

        await taskUsersAPI.create(workspaceId, projectId, taskId, { id, userId })
    }

    const handleUserDelete = async (user) => {
        const taskUserId = user.tuid
        const newUsers = users.filter((u) => user.id != u.id)

        actions.taskUpdate({ task: { id: taskId, users: newUsers } })

        await taskUsersAPI.remove(workspaceId, projectId, taskId, taskUserId)
    }

    const handleTaskFollowClick = async () => {
        if (isFollowing) {
            const taskFollower = followers.find((u) => u.id == getUserId())
            const taskFollowerId = taskFollower.tfid
            const newFollowers = followers.filter((u) => user.id != u.id)

            actions.taskUpdate({ task: { id, followers: newFollowers } })

            await taskFollowersAPI.remove(workspaceId, projectId, taskId, taskFollowerId)
        } else {
            const { id } = await taskFollowersAPI.create(workspaceId, projectId, taskId, { userId: user.id })

            const newFollowers = [
                ...followers,
                {
                    id: user.id,
                    name: user.name,
                    image: user.image,
                    tfid: id,
                },
            ]

            actions.taskUpdate({ task: { id: taskId, followers: newFollowers } })
        }
    }

    const handlePriorityChange = async (priority) => {
        actions.taskUpdate({ task: { id, priority } })

        await tasksAPI.update(workspaceId, projectId, taskId, { priority })
    }

    const handleUpdate = (html, json, editor) => {
        clearTimeout(timeout.current)
        timeout.current = setTimeout(() => handleDescriptionSave(html, json, editor), TIPTAP_CONTENT_SAVE_TIMEOUT)
    }

    const handleDescriptionSave = async (html, json, editor) => {
        const description = html
        const mentions = getMentionsfromTipTapJSON(json)

        actions.taskUpdate({ task: { id, description } })

        await tasksAPI.update(workspaceId, projectId, taskId, { description, mentions })
    }

    const onTitleChange = async (html) => {
        if (html == '') return
        if (html == title) return

        const entities = await processHTML(html)
        const task = cleanObject({
            title: entities.title,
            users: removeExistingElements(entities.users, users),
            labels: removeExistingElements(entities.labels, labels),
            start: entities.dates?.start,
            end: entities.dates?.end,
            repeat: entities.dates?.repeat,
            wholeDay: entities.dates?.wholeDay,
        })

        const updatedTask = await tasksAPI.updatev2(workspaceId, projectId, taskId, task)

        const updatedTaskObject = cleanObject({
            id,
            title: entities.title,
            users: [...users, ...updatedTask.users],
            labels: [...labels, ...updatedTask.labels],
            start: task.start,
            end: task.end,
            repeat: task.repeat,
            wholeDay: task.wholeDay,
        })

        editorRef.current.innerHTML = entities.title

        actions.taskUpdate({ task: { ...updatedTaskObject } })
    }

    // RichInputProvider

    const handleTrigger = async (text) => {
        const word = text.trim()
        if (word.charAt(0) == '@') {
            const username = word.substring(1) || ''
            const users = teamId
                ? await teamUsersAPI.getSearch(workspaceId, teamId, username)
                : projectId
                ? await projectUsersAPI.getSearch(workspaceId, projectId, username)
                : await workspaceUsersAPI.getSearch(workspaceId, username)

            setOptions(
                users
                    .map(({ id, image, username }) => ({
                        id,
                        color: undefined,
                        image,
                        name: '@' + username,
                        type: 'user',
                        value: username,
                    }))
                    .sort(byName)
            )
        } else if (word.charAt(0) == '#') {
            const label = word.substring(1) || ''
            const labels = await labelsAPI.getSearch(workspaceId, label)

            setOptions(
                labels
                    .map(({ id, text, color }) => ({
                        id,
                        color,
                        name: '#' + text,
                        type: 'label',
                        value: text,
                    }))
                    .sort(byName)
            )
        } else {
            setOptions([])
        }
    }

    // CommonProvider

    const handleUserFilter = async (query) => {
        setError(null)

        try {
            const users = teamId
                ? await teamUsersAPI.getSearch(workspaceId, teamId, query)
                : projectId
                ? await projectUsersAPI.getSearch(workspaceId, projectId, query)
                : await workspaceUsersAPI.getSearch(workspaceId, query)

            const present = users.find((u) => u.id == user.id)
            const availableUsers = present ? users.sort(byName) : [...users, user].sort(byName)

            setAvailableUsers(availableUsers)
            setOptions(users.sort(byName).map(({ id, username }) => ({ id, name: username, type: 'user' })))
        } catch (e) {
            logger(e)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleLabelFilter = async (query = '') => {
        setError(null)

        try {
            // const labels = await labelsAPI.getSearch(workspaceId, query)
            const labels = searchLabels(query)
            const newLabel = { id: null, text: LABEL_CREATE_PREFIX + query, color: getRandomColor() }
            const noDirectMatch = !labels.find(({ text }) => text.toLowerCase() == query.toLowerCase()) && !!query

            if (noDirectMatch) {
                setAvailableLabels([newLabel, ...labels.sort(byText)])
            } else {
                setAvailableLabels(labels.sort(byText))
            }
        } catch (e) {
            logger(e)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    // -------------

    const handleAttachmentDelete = async (attachment) => {
        actions.taskUpdate({ task: { id, ...task, attachments: attachments.filter((a) => attachment.id != a.id) } })
        //actions.taskUpdate({ task: { id, attachments: attachments.length - 1 } })

        taskAttachmentsAPI.remove(workspaceId, projectId, taskId, attachment.id)
    }

    const handleDismiss = () => {
        navigate(removeURLTask(window.location.href))

        actions.setTask({
            id: '',
            title: '',
            description: '',
            priority: null,
            type: null,
            position: 0,
            complete: false,
            points: 0,
            complexity: null,
            start: null,
            end: null,
            repeat: null,
            wholeDay: false,
            externalEmail: null,
            meta: [],
            tasks: [],
            users: [],
            followers: [],
            labels: [],
            attachments: [],
            likes: 0,
            projectId: '',
            projectSectionId: '',
            projectSectionTitle: '',
            projectName: '',
            teamId: '',
        })
        actions.setComments([])

        if (hideComplete && complete) {
            setTimeout(() => actions.taskDelete({ task: { id } }), 500)
        }
    }

    const setTaskAsRead = async (id) => {
        try {
            actions.taskUpdate({ task: { id, read: true } })
            await tasksAPI.update(workspaceId, projectId, id, { read: true })
        } catch (e) {
            logger(e)
        }
    }

    const populateSections = async (projectId) => {
        try {
            if (projectId) {
                const sections = await projectSectionsAPI.getAll(workspaceId, projectId)

                setSections([
                    { value: '', label: 'No section' },
                    ...sections.map(({ id, title }) => ({
                        value: id ?? '',
                        label: title,
                    })),
                ])
            }
        } catch (e) {
            logger(e)
        }
    }

    const populateSubtasks = async () => {
        try {
            const tasks = await tasksAPI.getSubtasks(workspaceId, projectId, taskId)
            actions.setSubtasks(buildTaskTree(tasks || []))
        } catch (e) {
            logger(e)
        }
    }

    const fetchData = async () => {
        setLoading(true)
        setError(null)

        try {
            const {
                id,
                title,
                description,
                priority,
                type,
                position,
                complete,
                read,
                points,
                complexity,
                start,
                end,
                repeat,
                wholeDay,
                externalEmail,
                parentTaskId,
                meta,
                users,
                followers,
                labels,
                attachments,
                likes,
                projectId,
                projectSectionId,
                projectSectionTitle,
                projectName,
                teamId,
            } = await tasksAPI.getOne(workspaceId, taskId)

            actions.setTask({
                id,
                title,
                description,
                priority,
                type,
                position: +position,
                complete,
                read,
                points,
                complexity,
                start,
                end,
                repeat,
                wholeDay,
                parentTaskId,
                externalEmail,
                meta: typeof meta == 'string' ? JSON.parse(meta) : {},
                users: users || [],
                followers: followers || [],
                labels: labels || [],
                attachments: attachments || [],
                likes,
                projectId,
                projectSectionId,
                projectSectionTitle,
                projectName,
                teamId,
            })

            populateSections(projectId)
            populateSubtasks()

            // setEndOfContentEditable(editorRef.current)
            // editorRef.current?.focus()
            // setScreenshot(meta[0])

            setTaskAsRead(id)
            setPointsInput(points)
            setLoading(false)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError(e.message)
        }
    }

    const renderMain = () => {
        return (
            <UploadArea
                row
                flex={1}
                height="100%"
                width="100%"
                onDrop={handleFileUpload}>
                {(over) => (
                    <div className="task__container f-scrollbar">
                        <Loading loading={loading} />

                        {over && (
                            <div className="task__droparea f-fadein f-col">
                                <Icon
                                    icon={TbFileUpload}
                                    color="var(--f-color-text-weak)"
                                    style={{
                                        '--f-icon-sizing-md': '7rem',
                                        '--f-icon-stroke-width-md': '1',
                                        'marginTop': -5,
                                    }}
                                />

                                <Heading
                                    as="h4"
                                    fontWeight={600}>
                                    Drop files here to upload
                                </Heading>
                            </div>
                        )}

                        {(type == 'milestone' || type == 'blocker') && (
                            <Notification variant={type == 'milestone' ? 'success' : 'danger'}>
                                <NotificationIcon>
                                    <Icon icon={type == 'milestone' ? TbFlag : TbAlertSquareRounded} />
                                </NotificationIcon>
                                <NotificationContent>
                                    This is a <strong style={{ color: 'inherit' }}>{type}</strong>
                                </NotificationContent>
                            </Notification>
                        )}

                        {!!externalEmail && (
                            <Notification variant="highlight">
                                <NotificationIcon>
                                    <Icon icon={TbMail} />
                                </NotificationIcon>
                                <NotificationContent>
                                    <Text>This was created by {externalEmail}</Text>
                                </NotificationContent>
                            </Notification>
                        )}

                        <div className="task__title f-row">
                            <Check
                                id={id}
                                priority={priority}
                                checked={complete}
                                onCheck={() => handleComplete(!complete)}
                            />

                            <div style={{ flex: 1, paddingRight: '1rem' }}>
                                <RichInput
                                    disabled={loading}
                                    style={{
                                        color: complete ? 'var(--f-color-text-weakest)' : undefined,
                                    }}
                                    dontClearOnSubmit
                                    defaultValue={title}
                                    ref={editorRef}
                                    placeholder="..."
                                    onSubmit={(title) => (title ? onTitleChange(title) : null)}
                                    onCancel={() => null}
                                    onIndent={() => null}
                                    onOutdent={() => null}
                                    onUp={() => null}
                                    onDown={() => null}
                                />
                            </div>
                        </div>

                        <div className="task__description f-row">
                            <div className="task__description-icon">
                                <Icon
                                    icon={FIDescription}
                                    size="sm"
                                />
                            </div>

                            <div className="task__description-text">
                                <Tiptap
                                    updateAfterBlur
                                    content={description}
                                    onUpdate={handleUpdate}
                                    workspaceId={workspaceId}
                                    projectId={projectId}
                                    teamId={teamId}
                                />
                            </div>
                        </div>

                        <div className="task__fields-container f-row">
                            <div className="task__fields f-col">
                                <div className="task__data-row f-row">
                                    <div className="task__data-row-field">
                                        <Text colorToken="text-weak">Assigned</Text>
                                    </div>
                                    <div className="task__data-row-value f-row">
                                        <UserButton
                                            users={users}
                                            onAdd={handleUserAdd}
                                            onDelete={handleUserDelete}
                                        />
                                    </div>
                                </div>

                                <div className="task__data-row f-row">
                                    <div className="task__data-row-field">
                                        <Text colorToken="text-weak">Due Date</Text>
                                    </div>
                                    <div className="task__data-row-value f-row">
                                        <DateButton
                                            portal={Portal}
                                            buttonStyleProps={{ flexDirection: 'row-reverse' }}
                                            complete={complete}
                                            start={start}
                                            end={end}
                                            repeat={repeat}
                                            wholeDay={wholeDay}
                                            onDelete={handleDateDelete}
                                            onDateChange={handleDateChange}
                                        />
                                    </div>
                                </div>

                                <div className="task__data-row f-row">
                                    <div className="task__data-row-field">
                                        <Text colorToken="text-weak">Priority</Text>
                                    </div>
                                    <div className="task__data-row-value f-row">
                                        <PriorityButton
                                            onChange={handlePriorityChange}
                                            title="Select project"
                                            priority={priority}
                                        />
                                    </div>
                                </div>

                                <div className="task__data-row f-row">
                                    <div className="task__data-row-field">
                                        <Text colorToken="text-weak">Labels</Text>
                                    </div>
                                    <div className="task__data-row-value f-row">
                                        <LabelButton
                                            labels={labels}
                                            onAdd={handleLabelAdd}
                                            onDelete={handleLabelDelete}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="task__fields f-col">
                                <div className="task__data-row f-row">
                                    <div className="task__data-row-field">
                                        <Text colorToken="text-weak">Followers</Text>
                                    </div>
                                    <div className="task__data-row-value f-row">
                                        <Users
                                            disabled
                                            users={followers}
                                            onClick={() => null}
                                        />
                                        <Button
                                            size="sm"
                                            m="0 0 0 -2px"
                                            height="1.65rem"
                                            variant={isFollowing ? 'accent' : undefined}
                                            onClick={handleTaskFollowClick}>
                                            {isFollowing ? 'Unfollow' : 'Follow'}
                                        </Button>
                                    </div>
                                </div>
                                {/* 
                                <div className="task__data-row f-row">
                                    <div className="task__data-row-field">
                                        <Text colorToken="text-weak">Project</Text>
                                    </div>
                                    <div className="task__data-row-value f-row">
                                        <ListButton
                                            onChange={handleProjectChange}
                                            title="Select project"
                                            list={selectedProject}
                                            options={selectableProjects}
                                            anchor="bottom-right"
                                        />
                                    </div>
                                </div>
                                */}
                                <div className="task__data-row f-row">
                                    <div className="task__data-row-field">
                                        <Text colorToken="text-weak">Section</Text>
                                    </div>
                                    <div className="task__data-row-value f-row">
                                        <ListButton
                                            onChange={handleSectionChange}
                                            title="Select section"
                                            list={selectedSection == -1 ? 0 : selectedSection}
                                            options={sections}
                                            anchor="bottom-right"
                                            disabled={!task.projectId || !!task.parentTaskId}
                                        />
                                    </div>
                                </div>

                                <div className="task__data-row f-row">
                                    <div className="task__data-row-field">
                                        <Text colorToken="text-weak">Complexity</Text>
                                    </div>
                                    <div className="task__data-row-value f-row">
                                        <ListButton
                                            onChange={handleComplexityUpdate}
                                            title="Complexity"
                                            list={complexityIndex}
                                            icon={TbExclamationMark}
                                            options={[
                                                { value: '0', label: 'Not set' },
                                                { value: '1', label: 'Low' },
                                                { value: '2', label: 'Medium' },
                                                { value: '3', label: 'High' },
                                            ]}
                                            anchor="bottom-right"
                                        />
                                    </div>
                                </div>

                                <div className="task__data-row f-row">
                                    <div className="task__data-row-field">
                                        <Text colorToken="text-weak">Points</Text>
                                    </div>
                                    <div className="task__data-row-value f-row">
                                        {!pointsEdit && (
                                            <Button
                                                size="sm"
                                                height="1.65rem"
                                                onClick={() => {
                                                    if (!points) {
                                                        handlePointsUpdate(1)
                                                        setPointsInput(1)
                                                    }
                                                    setPointsEdit(true)
                                                }}>
                                                {!!points ? points : 'Allocate points'}
                                            </Button>
                                        )}

                                        {pointsEdit && (
                                            <>
                                                <Input
                                                    selectOnFocus
                                                    autoFocus
                                                    type="number"
                                                    height="1.65rem"
                                                    width="4rem"
                                                    size="sm"
                                                    max={1000}
                                                    value={pointsInput}
                                                    onChange={(e) => setPointsInput(e.target.value)}
                                                    onBlur={(e) => handlePointsUpdate(+e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (getKey(e).isEnter) {
                                                            handlePointsUpdate(pointsInput)
                                                            setPointsEdit(false)
                                                        }
                                                    }}
                                                />
                                                <ButtonGroup m="0 0 0 -1px">
                                                    <Button
                                                        size="sm"
                                                        height="1.65rem"
                                                        width="2rem"
                                                        onClick={() => {
                                                            handlePointsUpdate(pointsInput)
                                                            setPointsEdit(false)
                                                        }}>
                                                        <Icon
                                                            icon={TbCheck}
                                                            size="sm"
                                                        />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        height="1.65rem"
                                                        width="3rem"
                                                        onClick={() => {
                                                            handlePointsUpdate(0)
                                                            setPointsEdit(false)
                                                        }}>
                                                        Clear
                                                    </Button>
                                                </ButtonGroup>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {!!attachments?.length && (
                            <div className="task__attachments f-row">
                                {attachments.map((attachment, index) => (
                                    <TaskAttachment
                                        key={index}
                                        attachment={attachment}
                                        onDelete={() => handleAttachmentDelete(attachment)}
                                        onScreenshotView={(screenshot) => {
                                            setScreenshot(screenshot)
                                        }}
                                    />
                                ))}
                            </div>
                        )}

                        <div className="task__subtasks">
                            <List />
                        </div>
                    </div>
                )}
            </UploadArea>
        )
    }

    useCustomEvent('reload', fetchData)
    useCustomEvent('reload:subtasks', populateSubtasks)

    useEffect(() => {
        if (taskId) fetchData()
    }, [taskId])

    useSubscription(taskId)

    return (
        <>
            {!!screenshot && (
                <Screenshot
                    meta={meta}
                    attachment={screenshot}
                    onDismiss={() => setScreenshot(null)}
                />
            )}

            <RichInputProvider
                triggers={['#', '@']}
                options={options}
                onTrigger={handleTrigger}>
                <CommonProvider
                    onUserFilter={handleUserFilter}
                    onLabelFilter={handleLabelFilter}
                    availableLabels={availableLabels}
                    availableUsers={availableUsers}
                    colors={colors}>
                    <TaskToolbar
                        onDismiss={handleDismiss}
                        onFileUpload={handleFileUpload}
                        location={location}
                        urlPattern={urlPattern}
                    />

                    <div className="task__outer f-row">
                        {direction == 'horizontal' && <SidebarArea>{renderMain()}</SidebarArea>}

                        {direction == 'vertical' && renderMain()}

                        <Comments
                            externalEmail={externalEmail}
                            taskId={taskId}
                            teamId={teamId}
                            projectId={projectId}
                            workspaceId={workspaceId}
                            isBelow={direction == 'vertical'}
                        />
                    </div>
                </CommonProvider>
            </RichInputProvider>
        </>
    )
}
